// ─── HADIAH PEMBELIAN — helper MURNI, tanpa I/O ─────────────────────────────
//
// Rancangan & putusan pemilik: docs/RENCANA-hadiah-pembelian.md (LANJUT Tahap 1
// 2026-10-26). Hadiah = baris `order_items` biasa ber-harga Rp 0 di pesanan
// produk hadiahnya; pengikat lintas pesanan = `order_gifts` (mig 8680). Server
// menghitung SENDIRI hak hadiah dari baris yang dipesan — angka di sini hanya
// tampilan & muatan pilihan varian. Cermin SQL:
//   • `giftEntitlement`     ↔ `gift_entitlement(...)`
//   • `giftFiltersOverlap`  ↔ `gift_filters_overlap(...)` (H5)
//   • `variantFitsGiftFilter` ↔ `bundle_variant_fits(...)` (dipakai ulang dari
//                              bundel: opsi terhapus = gagal-tutup)
//   • `orderGiftState`      ↔ `order_gift_state(...)`
//   • `giftTriggerPcs`      ↔ blok "Pcs syarat" `checkout_orders_with_bundles`
//   • `giftQuotaCap`        ↔ gerbang `GIFT_INVALID:quota` checkout (mig 8740)
// Tahap 2B (mig 8780, penanda internal `mig 8770` — RENCANA §14):
//   • `giftTargetCovers`        ↔ `gift_target_covers(...)` (= cakupan promo)
//   • `giftRuleCoversProduct`   ↔ `gift_rule_covers_product(...)`
//   • `giftRuleCoversLine`      ↔ `gift_rule_covers_line(...)`
//   • `giftLineOwner`           ↔ tambalan "pemilik baris" checkout (H24)
//   • `giftEntitlementMeasure`  ↔ `gift_entitlement_measure(...)`
//   • `giftLineValue`           ↔ nilai baris qty × (harga + potongan voucher) (H25)
//
// Putusan yang hidup di sini: H1 varian ber-Harga Tambahan tak bisa jadi hadiah
// (`giftRewardChoices` menandainya), H3 kelipatan per aturan, H6 baris bundel
// tak dihitung syarat (pemanggil hanya mengirim baris keranjang biasa), H24 satu
// barang satu aturan (tertua menang), H25 nilai = harga ditagih sebelum voucher.

import { variantFitsComponent } from "@/lib/bundle";
import { formatPrice } from "@/lib/pricing";
import {
  PROMO_GENERAL_SLOT,
  campaignCoversProduct,
  type PromoProductRef,
  type PromoProductTypeScope,
  type PromoTarget,
  type PromoTargetType
} from "@/lib/promo";

// ─── Tipe ────────────────────────────────────────────────────────────────────

/** Bentuk syarat (H23): `produk` = satu produk + batas varian (Tahap 1); `target` = beberapa produk / kategori / merek / semua produk. */
export type GiftTriggerScope = "produk" | "target";
/** Ukuran syarat (H23): `pcs` = minimal beli; `nilai` = minimal belanja rupiah (H25). */
export type GiftMeasure = "pcs" | "nilai";
/** Jenis target inklusif aturan bertarget; `all` = semua produk (± pengecualian). Bentuk promo. */
export type GiftTargetType = PromoTargetType;
export type GiftProductTypeScope = PromoProductTypeScope;
/** Satu baris target `{kind, productId, name, mode}` — bentuk `PromoTarget`, dipakai `TargetPicker` apa adanya. */
export type GiftTarget = PromoTarget;
/** Produk yang dinilai cakupannya: id + merek & kategori (BY NAMA) + tipe. */
export type GiftProductRef = PromoProductRef;

export type GiftRule = {
  id: string;
  storeId: string;
  name: string;
  description: string | null;
  /** NULL = produknya terhapus (FK set null) → aturan "tidak tersedia". */
  triggerProductId: string | null;
  /** `variant_options.id` yang menjadi syarat; kosong = semua varian. */
  triggerOptionIds: string[];
  triggerMinQty: number;
  rewardProductId: string | null;
  /** Batas varian hadiah; menyisakan tepat satu varian = dikunci. */
  rewardOptionIds: string[];
  rewardQty: number;
  /** H3: berlaku kelipatan (beli 4 pada "beli 2 gratis 1" = 2 hadiah). */
  repeat: boolean;
  /** Batas kelipatan; null = tanpa batas. Hanya bermakna bila `repeat`. */
  maxMultiplier: number | null;
  /** Kosong = semua pembeli; 0 = pelanggan umum; 1..15 = slot level. */
  levelSlots: number[];
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  /** Kuota PCS HADIAH seluruh pembeli (mig 8740); null = tak terbatas. */
  quotaTotal: number | null;
  /** Batas pcs hadiah per pembeli sepanjang umur aturan (H16); null = tak dibatasi. */
  perCustomerLimit: number | null;
  /** Bentuk syarat (mig 8780). Kolom NULL (aturan lama) = `produk`. */
  triggerScope: GiftTriggerScope;
  /** Jenis target aturan `target`; null untuk `produk`. Kolom NULL pada aturan `target` = `all`. */
  triggerTargetType: GiftTargetType | null;
  /** Saringan tipe produk aturan `target`; NULL = `all`. */
  triggerProductTypeScope: GiftProductTypeScope;
  /** Baris target aturan `target` (id produk huruf kecil); `[]` untuk `produk`. */
  triggerTargets: GiftTarget[];
  /** Ukuran syarat; NULL = `pcs`. */
  triggerMeasure: GiftMeasure;
  /** Minimal belanja (rupiah) ukuran `nilai`; null untuk `pcs`. */
  triggerMinAmount: number | null;
  /** Umur aturan — pemilik baris saat bentrok belakangan (H24: yang lebih dulu dibuat menang). */
  createdAt: string;
  /** "Berlaku juga di Kasir" (mig 8840, H35); kolom NULL = mati. */
  applyToPos: boolean;
};

/**
 * Kolom `gift_rules` — SATU string untuk pembaca storefront (anon, wajib
 * memfilter `store_id` sendiri — aturan A2) dan admin. Literal tunggal supaya
 * tipe supabase-js tetap terurai.
 */
export const GIFT_RULE_SELECT =
  "id, store_id, name, description, trigger_product_id, trigger_option_ids, trigger_min_qty, reward_product_id, reward_option_ids, reward_qty, repeat, max_multiplier, level_slots, is_active, starts_at, ends_at, quota_total, per_customer_limit, created_at, trigger_scope, trigger_target_type, trigger_product_type_scope, trigger_targets, trigger_measure, trigger_min_amount, apply_to_pos";

const toInt = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

const toIds = (v: unknown): string[] => (Array.isArray(v) ? v.map(String).filter(Boolean) : []);

/** Bilangan bulat > 0, selain itu null (kolom kuota: NULL = tak terbatas). */
const toPositiveOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = toInt(v, 0);
  return n > 0 ? n : null;
};

const GIFT_TARGET_TYPES: readonly GiftTargetType[] = ["product", "category", "brand", "all"];

/**
 * `trigger_targets` (jsonb) → baris target. Baris berjenis tak dikenal dibuang
 * (di SQL ia tak pernah cocok — hasilnya sama); id produk huruf kecil (cermin
 * `lower(productId) = id::text`); mode selain `exclude` = `include`.
 */
export function mapGiftTargets(raw: unknown): GiftTarget[] {
  if (!Array.isArray(raw)) return [];
  const out: GiftTarget[] = [];
  for (const t of raw) {
    if (!t || typeof t !== "object") continue;
    const row = t as { kind?: unknown; productId?: unknown; name?: unknown; mode?: unknown };
    if (row.kind !== "product" && row.kind !== "category" && row.kind !== "brand") continue;
    out.push({
      kind: row.kind,
      productId:
        row.kind === "product" && typeof row.productId === "string" && row.productId
          ? row.productId.toLowerCase()
          : null,
      name: String(row.name ?? "").trim(),
      mode: row.mode === "exclude" ? "exclude" : "include"
    });
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftRuleRow(row: any): GiftRule {
  const maxMultiplier = row?.max_multiplier;
  const scope: GiftTriggerScope = row?.trigger_scope === "target" ? "target" : "produk";
  const targetType = GIFT_TARGET_TYPES.find((t) => t === row?.trigger_target_type) ?? "all";
  const typeScope = row?.trigger_product_type_scope;
  return {
    id: String(row?.id ?? ""),
    storeId: String(row?.store_id ?? ""),
    name: String(row?.name ?? ""),
    description:
      typeof row?.description === "string" && row.description.trim() ? row.description : null,
    triggerProductId: row?.trigger_product_id ? String(row.trigger_product_id) : null,
    triggerOptionIds: toIds(row?.trigger_option_ids),
    triggerMinQty: Math.max(1, toInt(row?.trigger_min_qty, 1)),
    rewardProductId: row?.reward_product_id ? String(row.reward_product_id) : null,
    rewardOptionIds: toIds(row?.reward_option_ids),
    rewardQty: Math.max(1, toInt(row?.reward_qty, 1)),
    repeat: row?.repeat !== false,
    maxMultiplier:
      maxMultiplier === null || maxMultiplier === undefined || maxMultiplier === ""
        ? null
        : Math.max(1, toInt(maxMultiplier, 1)),
    levelSlots: Array.isArray(row?.level_slots)
      ? row.level_slots.map(Number).filter((n: number) => Number.isInteger(n))
      : [],
    isActive: row?.is_active === true,
    startsAt: row?.starts_at ?? null,
    endsAt: row?.ends_at ?? null,
    quotaTotal: toPositiveOrNull(row?.quota_total),
    perCustomerLimit: toPositiveOrNull(row?.per_customer_limit),
    triggerScope: scope,
    triggerTargetType: scope === "target" ? targetType : null,
    triggerProductTypeScope: typeScope === "preorder" || typeScope === "ready_stock" ? typeScope : "all",
    triggerTargets: scope === "target" ? mapGiftTargets(row?.trigger_targets) : [],
    triggerMeasure: row?.trigger_measure === "nilai" ? "nilai" : "pcs",
    triggerMinAmount: toPositiveOrNull(row?.trigger_min_amount),
    createdAt: String(row?.created_at ?? ""),
    applyToPos: row?.apply_to_pos === true
  };
}

// ─── Kelayakan & periode ────────────────────────────────────────────────────

/** Aturan sedang berjalan (aktif + dalam periode) — cermin gerbang checkout. */
export function isGiftRuleRunning(
  rule: Pick<GiftRule, "isActive" | "startsAt" | "endsAt">,
  now: Date
): boolean {
  if (!rule.isActive) return false;
  const t = now.getTime();
  if (!Number.isFinite(t)) return false;
  const start = rule.startsAt ? Date.parse(rule.startsAt) : NaN;
  const end = rule.endsAt ? Date.parse(rule.endsAt) : NaN;
  if (Number.isFinite(start) && t < start) return false;
  if (Number.isFinite(end) && t > end) return false;
  return true;
}

/**
 * Berhakkah pembeli ber-slot ini? Kosong = semua; pembeli tanpa level (tamu,
 * pelanggan umum, grup nonaktif) dinilai slot 0 — cermin `bundleAllowsLevel`.
 */
export function giftRuleAllowsLevel(
  rule: Pick<GiftRule, "levelSlots">,
  slot: number | null | undefined
): boolean {
  const slots = rule.levelSlots ?? [];
  if (!slots.length) return true;
  return slots.includes(slot ?? PROMO_GENERAL_SLOT);
}

// ─── Hak hadiah ──────────────────────────────────────────────────────────────

export type GiftQuantityRule = Pick<GiftRule, "triggerMinQty" | "rewardQty" | "repeat" | "maxMultiplier">;

/** Pcs hadiah dari pcs syarat. Cermin SQL `gift_entitlement`. */
export function giftEntitlement(triggerPcs: number, rule: GiftQuantityRule): number {
  const pcs = Number.isFinite(triggerPcs) ? Math.max(0, Math.floor(triggerPcs)) : 0;
  const min = Math.max(1, Math.floor(rule.triggerMinQty || 1));
  const reward = Math.max(1, Math.floor(rule.rewardQty || 1));
  if (pcs < min) return 0;
  if (!rule.repeat) return reward;
  const times = Math.floor(pcs / min);
  const cap = rule.maxMultiplier === null || rule.maxMultiplier === undefined ? times : rule.maxMultiplier;
  return Math.min(times, Math.max(1, Math.floor(cap))) * reward;
}

/**
 * Pcs syarat yang masih kurang menuju hak BERIKUTNYA — bahan ajakan "tambah N
 * pcs lagi". 0 = tak ada hak berikutnya (sekali saja sudah tercapai, atau batas
 * kelipatan sudah penuh).
 */
export function giftMissingPcs(triggerPcs: number, rule: GiftQuantityRule): number {
  const pcs = Number.isFinite(triggerPcs) ? Math.max(0, Math.floor(triggerPcs)) : 0;
  const min = Math.max(1, Math.floor(rule.triggerMinQty || 1));
  if (pcs < min) return min - pcs;
  if (!rule.repeat) return 0;
  const times = Math.floor(pcs / min);
  if (rule.maxMultiplier !== null && rule.maxMultiplier !== undefined && times >= rule.maxMultiplier) {
    return 0;
  }
  return (times + 1) * min - pcs;
}

// ─── Ukuran syarat: pcs | nilai belanja (mig 8780, H23/H25/H26) ──────────────

/** Plafon kelipatan ukuran nilai — cermin `least(…, 999999)` SQL. */
export const GIFT_TIMES_CAP = 999_999;

export type GiftMeasureRule = GiftQuantityRule & Partial<Pick<GiftRule, "triggerMeasure" | "triggerMinAmount">>;

const wholeNonNegative = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);

/**
 * Pcs hadiah dari ukuran syarat aturan: `pcs` = rumus Tahap 1 apa adanya;
 * `nilai` = 0 di bawah minimal belanja, sekali = pcs hadiah, kelipatan =
 * ⌊nilai / minimal⌋ dijepit batas kelipatan & 999.999 (H26). Cermin SQL
 * `gift_entitlement_measure`.
 */
export function giftEntitlementMeasure(rule: GiftMeasureRule, pcs: number, value: number): number {
  if ((rule.triggerMeasure ?? "pcs") !== "nilai") return giftEntitlement(pcs, rule);
  const min = rule.triggerMinAmount ?? 0;
  const v = wholeNonNegative(value);
  if (!(min > 0) || v < min) return 0;
  const reward = Math.max(1, Math.floor(rule.rewardQty || 1));
  if (!rule.repeat) return reward;
  const cap = rule.maxMultiplier === null || rule.maxMultiplier === undefined ? GIFT_TIMES_CAP : rule.maxMultiplier;
  return Math.min(Math.floor(v / min), cap, GIFT_TIMES_CAP) * reward;
}

/**
 * Rupiah belanja syarat yang masih kurang menuju hak BERIKUTNYA (ukuran nilai)
 * — bahan ajakan "tambah Rp X lagi". 0 = tak ada hak berikutnya.
 */
export function giftMissingAmount(value: number, rule: GiftMeasureRule): number {
  const min = rule.triggerMinAmount ?? 0;
  if (!(min > 0)) return 0;
  const v = wholeNonNegative(value);
  if (v < min) return min - v;
  if (!rule.repeat) return 0;
  const times = Math.floor(v / min);
  const cap = rule.maxMultiplier === null || rule.maxMultiplier === undefined ? GIFT_TIMES_CAP : rule.maxMultiplier;
  if (times >= Math.min(cap, GIFT_TIMES_CAP)) return 0;
  return (times + 1) * min - v;
}

/**
 * Nilai satu baris syarat = pcs × harga per pcs yang DITAGIH SEBELUM voucher
 * (promo, level, Harga Tambahan ikut — H25). Harga kosong / nol (harga
 * menyusul) = 0: tak dihitung dan tak ditandai syarat. Di server:
 * `qty × (unit_price + voucher_discount_per_pcs)`.
 */
export function giftLineValue(qty: number, unitPrice: number | null | undefined): number {
  const q = wholeNonNegative(qty);
  const p = Number(unitPrice);
  return q > 0 && Number.isFinite(p) && p > 0 ? q * p : 0;
}

// ─── Kuota hadiah (mig 8740) ────────────────────────────────────────────────

/** Sisa kuota satu aturan (`gift_rule_quota`); setiap null = tak terbatas. */
export type GiftQuota = {
  remainingTotal: number | null;
  perCustomerLimit: number | null;
  /** Sisa jatah pembeli yang login; null = tak dibatasi atau belum login. */
  remainingForMe: number | null;
};

export function giftRuleHasQuota(rule: Pick<GiftRule, "quotaTotal" | "perCustomerLimit">): boolean {
  return rule.quotaTotal !== null || rule.perCustomerLimit !== null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftQuotaRow(row: any): { ruleId: string; quota: GiftQuota } {
  const num = (v: unknown): number | null =>
    v === null || v === undefined || v === "" ? null : Math.max(0, toInt(v, 0));
  return {
    ruleId: String(row?.rule_id ?? ""),
    quota: {
      remainingTotal: num(row?.remaining_total),
      perCustomerLimit: num(row?.per_customer_limit),
      remainingForMe: num(row?.remaining_for_me)
    }
  };
}

/**
 * Pcs hadiah yang masih bisa diberikan ke pembeli ini: sisa kuota total dan
 * sisa jatahnya, mana yang lebih kecil. null = tak terbatas. Hak yang melebihi
 * angka ini diberikan sebatas sisanya (H15) — cermin gerbang `GIFT_INVALID:quota`.
 */
export function giftQuotaCap(quota: GiftQuota | null | undefined): number | null {
  if (!quota) return null;
  const caps = [quota.remainingTotal, quota.remainingForMe].filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n)
  );
  return caps.length ? Math.max(0, Math.min(...caps)) : null;
}

/** Sisa hadiah dipajang ke pembeli hanya bila tinggal sedikit (rencana §13.1). */
export const GIFT_QUOTA_SHOW_AT = 20;

/** "Sisa 3 pcs" — null bila tak terbatas, habis, atau masih banyak. */
export function giftRemainingLabel(remaining: number | null | undefined): string | null {
  if (typeof remaining !== "number" || remaining <= 0 || remaining > GIFT_QUOTA_SHOW_AT) return null;
  return `Sisa ${remaining} pcs`;
}

// ─── Batas varian ────────────────────────────────────────────────────────────

/**
 * Lolos batas varian aturan? Semantik SAMA dengan komponen bundel (cermin
 * `bundle_variant_fits`): untuk setiap grup yang dibatasi, varian wajib memakai
 * salah satu opsi terpilih; kosong = semua varian; opsi yang tak lagi ada di
 * produk = gagal-tutup.
 */
export function variantFitsGiftFilter(
  filterOptionIds: readonly string[],
  variantOptionIds: readonly string[],
  optionGroupOf: ReadonlyMap<string, string>
): boolean {
  return variantFitsComponent({ allowedOptionIds: [...filterOptionIds] }, variantOptionIds, optionGroupOf);
}

/**
 * Dua batas varian (syarat) bisa dipenuhi varian yang SAMA? = untuk setiap grup
 * yang dibatasi KEDUANYA ada opsi bersama; grup yang dibatasi satu sisi saja tak
 * memisahkan. Opsi yang tak dikenal diabaikan (konservatif: lebih sering
 * "beririsan"). Cermin SQL `gift_filters_overlap` — dasar H5.
 */
export function giftFiltersOverlap(
  a: readonly string[],
  b: readonly string[],
  optionGroupOf: ReadonlyMap<string, string>
): boolean {
  const byGroup = (ids: readonly string[]) => {
    const out = new Map<string, Set<string>>();
    for (const id of ids) {
      const g = optionGroupOf.get(id);
      if (!g) continue;
      const set = out.get(g) ?? new Set<string>();
      set.add(id);
      out.set(g, set);
    }
    return out;
  };
  const ga = byGroup(a);
  const gb = byGroup(b);
  for (const [group, setA] of ga) {
    const setB = gb.get(group);
    if (!setB) continue;
    let shared = false;
    for (const id of setA) {
      if (setB.has(id)) {
        shared = true;
        break;
      }
    }
    if (!shared) return false;
  }
  return true;
}

const periodsOverlap = (
  a: Pick<GiftRule, "startsAt" | "endsAt">,
  b: Pick<GiftRule, "startsAt" | "endsAt">
): boolean => {
  const start = (s: string | null) => (s ? Date.parse(s) : -Infinity);
  const end = (e: string | null) => (e ? Date.parse(e) : Infinity);
  // Rentang [mulai, selesai) — cermin `tstzrange(..., '[)')`.
  return start(a.startsAt) < end(b.endsAt) && start(b.startsAt) < end(a.endsAt);
};

/**
 * Aturan AKTIF lain yang bentrok dengan `rule` (H5): produk syarat sama, periode
 * bersinggungan, batas varian beririsan — DUA aturan produk tunggal saja.
 * Bentrok yang melibatkan aturan bertarget (H24) dinilai atas katalog di
 * server (`admin_gift_rule_preview`); server tetap penentunya.
 */
export function giftRuleClash<
  R extends Pick<GiftRule, "id" | "isActive" | "triggerProductId" | "triggerOptionIds" | "startsAt" | "endsAt"> &
    Partial<Pick<GiftRule, "triggerScope">>
>(rule: R, others: readonly R[], optionGroupOf: ReadonlyMap<string, string>): R | null {
  if (rule.triggerScope === "target") return null;
  for (const other of others) {
    if (other.id === rule.id || !other.isActive) continue;
    if (other.triggerScope === "target") continue;
    if (!rule.triggerProductId || other.triggerProductId !== rule.triggerProductId) continue;
    if (!periodsOverlap(rule, other)) continue;
    if (giftFiltersOverlap(rule.triggerOptionIds, other.triggerOptionIds, optionGroupOf)) return other;
  }
  return null;
}

// ─── Cakupan & pemilik baris (mig 8780, H23/H24) ─────────────────────────────

export type GiftCoverRule = Pick<
  GiftRule,
  "triggerScope" | "triggerProductId" | "triggerOptionIds" | "triggerTargetType" | "triggerProductTypeScope" | "triggerTargets"
>;

/**
 * Target aturan BERTARGET mencakup produk: tipe produk dulu, lalu pengecualian
 * (apa pun jenisnya membatalkan), lalu target inklusif sejenis — kategori &
 * merek BY NAMA. Semantik promo apa adanya (`campaignCoversProduct`); cermin
 * SQL `gift_target_covers`.
 */
export function giftTargetCovers(
  rule: Pick<GiftRule, "triggerTargetType" | "triggerProductTypeScope" | "triggerTargets">,
  product: GiftProductRef
): boolean {
  return campaignCoversProduct(
    {
      productTypeScope: rule.triggerProductTypeScope ?? "all",
      targetType: rule.triggerTargetType ?? "all",
      targets: rule.triggerTargets ?? []
    },
    { ...product, id: product.id.toLowerCase() }
  );
}

/** Aturan mencakup PRODUK (tanpa melihat varian) — bahan bentrok & blok halaman produk. Cermin `gift_rule_covers_product`. */
export function giftRuleCoversProduct(rule: GiftCoverRule, product: GiftProductRef): boolean {
  if (rule.triggerScope !== "target") return Boolean(rule.triggerProductId) && product.id === rule.triggerProductId;
  return giftTargetCovers(rule, product);
}

/** Varian satu baris keranjang: id (null = produk tanpa varian) + `option_ids` (null = tak dikenal). */
export type GiftLineRef = { variantId: string | null; optionIds: readonly string[] | null };

/**
 * Aturan mencakup BARIS keranjang. Produk tunggal: predikat Tahap 1 apa adanya
 * (varian tak dikenal = gagal-tutup; baris tanpa varian hanya cocok dengan
 * aturan tanpa batas varian). Bertarget: seluruh varian produk yang tercakup.
 * Cermin `gift_rule_covers_line`.
 */
export function giftRuleCoversLine(
  rule: GiftCoverRule,
  product: GiftProductRef,
  line: GiftLineRef,
  optionGroupOf: ReadonlyMap<string, string>
): boolean {
  if (rule.triggerScope === "target") return giftTargetCovers(rule, product);
  if (!rule.triggerProductId || product.id !== rule.triggerProductId) return false;
  if (!line.variantId) return rule.triggerOptionIds.length === 0;
  return line.optionIds !== null && variantFitsGiftFilter(rule.triggerOptionIds, line.optionIds, optionGroupOf);
}

/**
 * Kunci urut `created_at` sampai mikrodetik (PostgREST memulangkan 6 digit;
 * `Date` hanya milidetik). Tak terbaca = paling muda.
 */
function giftTimestampKey(iso: string): number {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return Number.POSITIVE_INFINITY;
  const frac = /\.(\d+)/.exec(iso)?.[1] ?? "";
  return ms * 1000 + Number(`${frac}000000`.slice(3, 6));
}

/** Urutan umur aturan (H24): yang lebih dulu dibuat lebih dulu, seri → id. Cermin `order by created_at, id`. */
export function compareGiftRuleAge(a: Pick<GiftRule, "createdAt" | "id">, b: Pick<GiftRule, "createdAt" | "id">): number {
  const ka = giftTimestampKey(a.createdAt);
  const kb = giftTimestampKey(b.createdAt);
  if (ka !== kb) return ka < kb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Aturan yang berebut baris keranjang: berjalan & berhak level pembeli ini,
 * urut umur. Seluruh aturan toko ikut — termasuk yang hadiahnya sedang tak bisa
 * diberikan — karena server menilai pemilik atas semuanya.
 */
export function giftOwnerCandidates<R extends GiftRule>(
  rules: readonly R[],
  ctx: { slot: number | null; now: Date }
): R[] {
  return rules.filter((r) => isGiftRuleRunning(r, ctx.now) && giftRuleAllowsLevel(r, ctx.slot)).sort(compareGiftRuleAge);
}

/**
 * PEMILIK satu baris keranjang BIASA (H24): kandidat pertama (tertua) yang
 * mencakupnya; null = tak ada. Baris hanya dihitung untuk pemiliknya — pembeli
 * tak pernah dapat dua hadiah dari satu barang. Cermin tambalan pemilik
 * `checkout_orders_with_bundles` (mig 8780).
 */
export function giftLineOwner<R extends GiftCoverRule>(
  candidates: readonly R[],
  product: GiftProductRef,
  line: GiftLineRef,
  optionGroupOf: ReadonlyMap<string, string>
): R | null {
  for (const rule of candidates) {
    if (giftRuleCoversLine(rule, product, line, optionGroupOf)) return rule;
  }
  return null;
}

// ─── Keranjang ───────────────────────────────────────────────────────────────

/** Satu baris keranjang BIASA (bukan bundel — H6) untuk menghitung syarat. */
export type GiftCartLine = {
  productId: string;
  /** Id varian; null = produk tanpa varian (hanya cocok dengan aturan tanpa batas). */
  variantId: string | null;
  qty: number;
};

/**
 * Pcs syarat di keranjang: Σ qty baris produk syarat yang variannya lolos batas.
 * `variantOptionIdsOf` memetakan id varian → `option_ids`-nya; varian yang tak
 * dikenal tak dihitung (gagal-tutup, cermin `pvx.id is not null`).
 */
export function giftTriggerPcs(
  rule: Pick<GiftRule, "triggerProductId" | "triggerOptionIds">,
  lines: readonly GiftCartLine[],
  variantOptionIdsOf: ReadonlyMap<string, readonly string[]>,
  optionGroupOf: ReadonlyMap<string, string>
): number {
  if (!rule.triggerProductId) return 0;
  let pcs = 0;
  for (const line of lines) {
    if (line.productId !== rule.triggerProductId) continue;
    const qty = Number.isFinite(line.qty) ? Math.max(0, Math.floor(line.qty)) : 0;
    if (!qty) continue;
    if (!line.variantId) {
      if (!rule.triggerOptionIds.length) pcs += qty;
      continue;
    }
    const optionIds = variantOptionIdsOf.get(line.variantId);
    if (!optionIds) continue;
    if (variantFitsGiftFilter(rule.triggerOptionIds, optionIds, optionGroupOf)) pcs += qty;
  }
  return pcs;
}

// ─── Pilihan varian hadiah ───────────────────────────────────────────────────

export type GiftRewardVariant = {
  id: string;
  name: string;
  optionIds: string[];
  /** Harga Tambahan varian (mig 7900); > 0 = tak bisa jadi hadiah (H1). */
  priceExtra: number;
  /** Stok Ready Stock; null = tak dibatasi (Pre-Order). */
  stock: number | null;
};

export type GiftRewardBlock = "harga_tambahan" | "stok_habis";

export type GiftRewardChoice = GiftRewardVariant & {
  /** Boleh dipilih sebagai hadiah untuk jumlah `need` pcs. */
  selectable: boolean;
  /** Sebab tak bisa dipilih; null = bisa. */
  block: GiftRewardBlock | null;
};

/**
 * Varian produk hadiah yang lolos batas varian aturan, urut seperti diberikan.
 * Varian ber-Harga Tambahan tetap DIPAJANG dengan tanda (H1: pemilik meminta
 * peringatan saat varian itu dipilih), tapi tak bisa dipilih.
 * `need` = pcs hadiah yang diminta dari satu varian (bahan pemeriksaan stok).
 */
export function giftRewardChoices(
  rule: Pick<GiftRule, "rewardOptionIds">,
  variants: readonly GiftRewardVariant[],
  optionGroupOf: ReadonlyMap<string, string>,
  need = 1
): GiftRewardChoice[] {
  return variants
    .filter((v) => variantFitsGiftFilter(rule.rewardOptionIds, v.optionIds, optionGroupOf))
    .map((v) => {
      const block: GiftRewardBlock | null =
        v.priceExtra > 0
          ? "harga_tambahan"
          : v.stock !== null && v.stock < Math.max(1, need)
            ? "stok_habis"
            : null;
      return { ...v, selectable: block === null, block };
    });
}

/**
 * Varian hadiah DIKUNCI admin: batasnya menyisakan tepat satu varian (yang
 * bukan ber-Harga Tambahan). null = pembeli memilih.
 */
export function lockedGiftReward(choices: readonly GiftRewardChoice[]): GiftRewardChoice | null {
  const usable = choices.filter((c) => c.block !== "harga_tambahan");
  return usable.length === 1 ? usable[0] : null;
}

export type GiftClaimLine = { variantId: string; qty: number };

export function giftClaimTotal(lines: readonly GiftClaimLine[]): number {
  return lines.reduce((s, l) => s + (Number.isFinite(l.qty) ? Math.max(0, Math.floor(l.qty)) : 0), 0);
}

/**
 * Pilihan bawaan: seluruh hak ke varian pertama yang stoknya cukup; kalau tak
 * ada, dibagi ke varian yang masih berstok. [] = tak ada varian yang bisa
 * diberikan (hadiah habis / semuanya ber-Harga Tambahan).
 */
export function defaultGiftClaim(
  entitlement: number,
  choices: readonly GiftRewardChoice[]
): GiftClaimLine[] {
  const want = Math.max(0, Math.floor(entitlement));
  if (!want) return [];
  const usable = choices.filter((c) => c.block !== "harga_tambahan");
  const whole = usable.find((c) => c.stock === null || c.stock >= want);
  if (whole) return [{ variantId: whole.id, qty: want }];
  const out: GiftClaimLine[] = [];
  let left = want;
  for (const c of usable) {
    if (!left) break;
    const take = Math.min(left, Math.max(0, c.stock ?? left));
    if (take > 0) {
      out.push({ variantId: c.id, qty: take });
      left -= take;
    }
  }
  return out;
}

/**
 * Pilihan pembeli dirapikan terhadap hak & varian terkini: varian yang tak lagi
 * bisa dipilih dibuang, jumlah dijepit stok & hak (urutan pilihan dipertahankan).
 * Kosong sesudah dirapikan → pilihan bawaan.
 */
export function normalizeGiftClaim(
  claim: readonly GiftClaimLine[] | null | undefined,
  entitlement: number,
  choices: readonly GiftRewardChoice[]
): GiftClaimLine[] {
  const want = Math.max(0, Math.floor(entitlement));
  if (!want) return [];
  const byId = new Map(choices.map((c) => [c.id, c] as const));
  const out: GiftClaimLine[] = [];
  let left = want;
  for (const line of claim ?? []) {
    const choice = byId.get(line.variantId);
    if (!choice || choice.block === "harga_tambahan" || !left) continue;
    const qty = Math.min(
      left,
      Math.max(0, Math.floor(line.qty)),
      choice.stock === null ? Infinity : Math.max(0, choice.stock)
    );
    if (qty > 0) {
      const existing = out.find((l) => l.variantId === line.variantId);
      if (existing) existing.qty += qty;
      else out.push({ variantId: line.variantId, qty });
      left -= qty;
    }
  }
  return out.length ? out : defaultGiftClaim(want, choices);
}

/**
 * Klaim dipenuhi sampai hak (H40, mig 8860): hadiah selalu ikut bila berhak.
 * Kosong = pilihan bawaan (`defaultGiftClaim`); sebagian = sisanya diisi ke
 * varian yang sudah dipilih dulu, lalu varian lain urut pilihan (tanpa Harga
 * Tambahan, stok cukup). Stok kurang = sebanyak yang bisa (server penentunya).
 */
export function fillGiftClaim(
  claim: readonly GiftClaimLine[],
  entitlement: number,
  choices: readonly GiftRewardChoice[]
): GiftClaimLine[] {
  const want = Math.max(0, Math.floor(entitlement));
  const out = claim.filter((l) => l.qty > 0).map((l) => ({ ...l }));
  if (!out.length) return defaultGiftClaim(want, choices);
  let left = want - out.reduce((s, l) => s + l.qty, 0);
  const chosen = new Set(out.map((l) => l.variantId));
  const order = [...choices.filter((c) => chosen.has(c.id)), ...choices.filter((c) => !chosen.has(c.id))];
  for (const c of order) {
    if (left <= 0) break;
    if (c.block === "harga_tambahan") continue;
    const line = out.find((l) => l.variantId === c.id);
    const room = c.stock === null ? left : Math.max(0, c.stock - (line?.qty ?? 0));
    const take = Math.min(left, room);
    if (take <= 0) continue;
    if (line) line.qty += take;
    else out.push({ variantId: c.id, qty: take });
    left -= take;
  }
  return out;
}

/** Muatan `p_gifts` RPC `checkout_orders_with_bundles` (mig 8680). */
export function giftCheckoutPayload(
  claims: readonly { ruleId: string; lines: readonly GiftClaimLine[] }[]
): Array<{ rule_id: string; lines: Array<{ variant_id: string; qty: number }> }> {
  return claims
    .map((c) => ({
      rule_id: c.ruleId,
      lines: c.lines
        .filter((l) => l.variantId && Number.isFinite(l.qty) && l.qty > 0)
        .map((l) => ({ variant_id: l.variantId, qty: Math.floor(l.qty) }))
    }))
    .filter((c) => c.rule_id && c.lines.length > 0);
}

// ─── Keadaan pembelian (diturunkan saat baca) ────────────────────────────────

export type OrderGiftState = "utuh" | "syarat_berkurang" | "batal";

/**
 * `batal` = nol pcs hadiah hidup; `syarat_berkurang` = hak dari syarat hidup
 * (pcs, atau nilainya untuk ukuran nilai — mig 8780) lebih kecil dari pcs
 * hadiah hidup (syarat dibatalkan toko — H7: hadiah tetap, toko menanggung);
 * selain itu `utuh`. Cermin SQL `order_gift_state`.
 */
export function orderGiftState(
  rule: GiftMeasureRule,
  liveTriggerPcs: number,
  liveGiftPcs: number,
  liveTriggerValue = 0
): OrderGiftState {
  if (!(liveGiftPcs > 0)) return "batal";
  return giftEntitlementMeasure(rule, liveTriggerPcs, liveTriggerValue) < liveGiftPcs ? "syarat_berkurang" : "utuh";
}

// ─── Teks ─────────────────────────────────────────────────────────────────────
// Dipakai pembeli DAN admin → tanpa kata ganti (docs/GAYA-BAHASA.md §1).

export type GiftRuleNames = {
  /**
   * Nama produk syarat + opsi syaratnya, mis. "Tas Merah" — aturan produk
   * tunggal. Aturan bertarget memakai `giftTargetDescription` (boleh "").
   */
  trigger: string;
  /** Nama produk hadiah (+ varian bila dikunci), mis. "Dompet Biru". */
  reward: string;
};

export type GiftSentenceRule = GiftMeasureRule &
  Partial<Pick<GiftRule, "triggerScope" | "triggerTargetType" | "triggerProductTypeScope" | "triggerTargets">>;

const GIFT_TYPE_LABEL: Record<"preorder" | "ready_stock", string> = {
  preorder: "Pre-Order",
  ready_stock: "Ready Stock"
};

/** "A" · "A & B" · "A, B & 3 lainnya". */
function giftNameList(names: readonly string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? "";
  if (clean.length === 2) return `${clean[0]} & ${clean[1]}`;
  return `${clean[0]}, ${clean[1]} & ${clean.length - 2} lainnya`;
}

const giftTargetName = (t: GiftTarget): string =>
  t.kind === "category" ? `kategori ${t.name}` : t.kind === "brand" ? `merek ${t.name}` : t.name;

/**
 * Barang syarat aturan BERTARGET dalam kalimat: "merek Aira", "kategori Gamis &
 * Tunik", "Gamis A, Gamis C & 2 lainnya", "produk Pre-Order apa saja" — plus
 * "kecuali …" bila ada pengecualian.
 */
export function giftTargetDescription(
  rule: Partial<Pick<GiftRule, "triggerTargetType" | "triggerProductTypeScope" | "triggerTargets">>
): string {
  const type = rule.triggerTargetType ?? "all";
  const typeScope = rule.triggerProductTypeScope ?? "all";
  const targets = rule.triggerTargets ?? [];
  const include = targets.filter((t) => t.mode !== "exclude" && t.kind === type).map((t) => t.name);
  const exclude = targets.filter((t) => t.mode === "exclude");
  let desc: string;
  if (type === "all") {
    desc = typeScope === "all" ? "produk apa saja" : `produk ${GIFT_TYPE_LABEL[typeScope]} apa saja`;
  } else {
    const names = giftNameList(include);
    desc = type === "category" ? `kategori ${names}` : type === "brand" ? `merek ${names}` : names;
    if (typeScope !== "all") desc += ` (${GIFT_TYPE_LABEL[typeScope]})`;
  }
  if (exclude.length) desc += ` kecuali ${giftNameList(exclude.map(giftTargetName))}`;
  return desc;
}

/**
 * Kalimat aturan:
 *   • produk tunggal × pcs : "Beli 2 Tas Merah, gratis 1 Dompet Biru"
 *   • bertarget × pcs      : "Beli 3 pcs merek Aira, gratis 1 Pouch" (+ "(boleh campur)" bila targetnya > 1)
 *   • × nilai              : "Belanja Rp 500.000 dari kategori Gamis, gratis 1 Pouch"; semua produk tanpa
 *                            saringan → "Belanja Rp 500.000, gratis 1 Pouch"
 * (+ " · berlaku kelipatan" / ", maks. N×").
 */
export function giftRuleSentence(rule: GiftSentenceRule, names: GiftRuleNames): string {
  const reward = `gratis ${rule.rewardQty} ${names.reward}`;
  const target = rule.triggerScope === "target";
  const desc = target ? giftTargetDescription(rule) : names.trigger;
  let base: string;
  if ((rule.triggerMeasure ?? "pcs") === "nilai") {
    const amount = formatPrice(rule.triggerMinAmount ?? 0);
    const plainAll =
      target &&
      (rule.triggerTargetType ?? "all") === "all" &&
      (rule.triggerProductTypeScope ?? "all") === "all" &&
      !(rule.triggerTargets ?? []).some((t) => t.mode === "exclude");
    base = plainAll ? `Belanja ${amount}, ${reward}` : `Belanja ${amount} dari ${desc}, ${reward}`;
  } else if (target) {
    const includes = (rule.triggerTargets ?? []).filter(
      (t) => t.mode !== "exclude" && t.kind === (rule.triggerTargetType ?? "all")
    ).length;
    base = `Beli ${rule.triggerMinQty} pcs ${desc}${includes > 1 ? " (boleh campur)" : ""}, ${reward}`;
  } else {
    base = `Beli ${rule.triggerMinQty} ${desc}, ${reward}`;
  }
  if (!rule.repeat) return base;
  if (rule.maxMultiplier !== null && rule.maxMultiplier !== undefined) {
    return `${base} · berlaku kelipatan, maks. ${rule.maxMultiplier}×`;
  }
  return `${base} · berlaku kelipatan`;
}

/**
 * Nama produk + opsi terpilih, mis. "Koleksi Aira" + ["Merah", "Tas"] → "Koleksi
 * Aira Tas Merah". Urutan opsi = urutan `optionNameOf` (pemuat menyusunnya urut
 * grup & opsi produk), bukan urutan admin mencentang. Opsi tak dikenal dilewati.
 */
export function giftTargetLabel(
  productName: string,
  optionIds: readonly string[],
  optionNameOf: ReadonlyMap<string, string>
): string {
  const wanted = new Set(optionIds);
  const names = [...optionNameOf.entries()]
    .filter(([id, name]) => wanted.has(id) && Boolean(name))
    .map(([, name]) => name);
  return [productName.trim(), ...names].filter(Boolean).join(" ");
}

/** Label peran baris pesanan (chip). */
export function giftRoleLabel(role: string | null | undefined): string | null {
  if (role === "hadiah") return "Hadiah";
  if (role === "syarat") return "Syarat hadiah";
  return null;
}

/** Kalimat alasan varian hadiah tak bisa dipilih. */
export function giftRewardBlockText(block: GiftRewardBlock): string {
  return block === "harga_tambahan"
    ? "Varian ini punya Harga Tambahan, jadi tidak bisa dipilih sebagai hadiah."
    : "Stok varian ini tidak cukup untuk hadiah.";
}

/**
 * Galat RPC hadiah (mig 8680, `quota` mig 8740) → kalimat layar. `null` = bukan galat hadiah.
 * Data layer meneruskan galat `GIFT_INVALID:<kode>` dengan nama aturan setelah
 * tanda `|` (detail SQL) supaya kalimatnya bisa menyebut hadiahnya.
 */
export function giftErrorMessage(raw: string | null | undefined): string | null {
  const msg = String(raw ?? "");
  const m = msg.match(/GIFT_INVALID:([a-z_]+)(?:\|(.*))?/);
  if (m) {
    const name = (m[2] ?? "").trim();
    const what = name ? `Hadiah "${name}"` : "Hadiah di keranjang";
    switch (m[1]) {
      case "inactive":
        return `${what} sudah berakhir atau dinonaktifkan.`;
      case "level":
        return `${what} tidak berlaku untuk level akun ini.`;
      case "unavailable":
        return `${what} sedang tidak bisa dipesan.`;
      case "not_eligible":
        return `Isi keranjang belum memenuhi syarat ${name ? `hadiah "${name}"` : "hadiah"}.`;
      case "variant":
        return `Pilihan varian ${name ? `hadiah "${name}"` : "hadiah"} sudah tidak tersedia. Pilih ulang variannya.`;
      case "price_extra":
        return "Varian ber-Harga Tambahan tidak bisa jadi hadiah. Pilih varian lain.";
      case "stock":
        return `Stok ${name ? `hadiah "${name}"` : "hadiah"} tidak cukup.`;
      case "quota":
        return `Sisa ${name ? `hadiah "${name}"` : "hadiah"} sudah berkurang. Periksa hadiahnya, lalu pesan lagi.`;
      case "duplicate":
      case "entitlement":
      default:
        return `Jumlah ${name ? `hadiah "${name}"` : "hadiah"} berubah. Periksa keranjang, lalu pesan lagi.`;
    }
  }
  // H27 (mig 8780): hadiah hanya boleh di nota yang memuat SEMUA syarat yang
  // belum lunas. Kalimat Tahap 1 tetap dikenali (tab yang belum dimuat ulang).
  if (/HADIAH_NOTA_UTUH|baru bisa ditagih bersama semua barang syarat|harus ditagih di satu (?:nota|tagihan) bersama semua barang syarat/.test(msg)) {
    const hit =
      msg.match(/Hadiah "[^"]*" baru bisa ditagih bersama semua barang syaratnya yang belum lunas\./) ??
      msg.match(/Barang hadiah "[^"]*" harus ditagih di satu (?:nota|tagihan) bersama semua barang syarat dan hadiahnya yang belum lunas\./);
    return hit ? hit[0] : "Hadiah baru bisa ditagih bersama semua barang syaratnya yang belum lunas.";
  }
  // H28 (mig 8780): batal sebagian yang menggugurkan hadiah — kalimat server apa adanya.
  if (/memuat hadiah pembelian|adalah hadiah pembelian|seluruh barang hadiah|tidak lagi memenuhi hadiah/.test(msg)) {
    return msg;
  }
  return null;
}
